import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { NormalBalanceChoice, NormalBalanceChoiceConflict } from '@releve/shared';
import { NormalBalanceChoiceDialog } from './normal-balance-choice-dialog';

type Resolve = (choice: NormalBalanceChoice | null) => void;

/**
 * `ask(conflicts)` opens the "manual normal-balance override" prompt
 * (NormalBalanceChoiceDialog) and resolves with the answer (`null` when
 * cancelled); render `dialog` once in the caller. The dialog is mounted only
 * while a question is open. A second `ask()` while one is open answers the
 * first with `null` (cancelled) and shows the new question; unmounting the
 * caller answers an open question with `null` too, so no promise is left
 * hanging. docs/CHART_OF_ACCOUNTS.md "Contra subtypes".
 */
export function useNormalBalanceChoice(options: { showAccountNumbers?: boolean } = {}): {
  ask: (conflicts: NormalBalanceChoiceConflict[]) => Promise<NormalBalanceChoice | null>;
  dialog: ReactNode;
} {
  const [conflicts, setConflicts] = useState<NormalBalanceChoiceConflict[] | null>(null);
  // The open question's resolver: a ref, so a second ask() can settle the
  // first synchronously (no side effects inside a state updater).
  const resolveRef = useRef<Resolve | null>(null);

  const settle = useCallback((choice: NormalBalanceChoice | null) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(choice);
  }, []);

  const ask = useCallback(
    (next: NormalBalanceChoiceConflict[]) =>
      new Promise<NormalBalanceChoice | null>((resolve) => {
        settle(null);
        resolveRef.current = resolve;
        setConflicts(next);
      }),
    [settle],
  );

  useEffect(() => () => settle(null), [settle]);

  const dialog = conflicts ? (
    <NormalBalanceChoiceDialog
      conflicts={conflicts}
      showAccountNumbers={options.showAccountNumbers ?? false}
      onChoose={(choice) => {
        settle(choice);
        setConflicts(null);
      }}
    />
  ) : null;

  return { ask, dialog };
}
